import {
  css, type Theme,
} from '@emotion/react';
import {
  faChevronDown, faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { NumberInCircleComponent } from '~/_shared/baseComponents/numberInShape/numberInCircle.component';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { type ActiveBoundaryFillInfoEntities } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryFillEntities';

type ActiveBoundaryDropdownTrigger = Readonly<{
  backgroundColor: string;
  entities: ActiveBoundaryFillInfoEntities;
  fillOrderNumber: number | null;
  foregroundColor: string;
  onClick?: () => void;
}>;

const ddTriggerStyles = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.highlight,
  paddingBlock: 4,
  paddingInline: 12,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  userSelect: 'none',
  borderTop: `1px solid ${theme.lineColors.basePrimary}`,
  minHeight: 32,
});

const interactiveDdTriggerStyles = (theme: Theme) => css({
  ':hover': {
    backgroundColor: theme.backgroundColors.highlightHover,
  },
});

const entityWrapperStyles = css({
  maxWidth: '100%',
  minWidth: 0,
});

const mainEntityStyles = (theme: Theme) => css({
  color: theme.textColors.primary,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
});

const subEntityStyles = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: 12,
  paddingTop: 2,
});

const ellipsisStyle = css({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

const entityChevronStyle = css({
  paddingLeft: 4,
});

const chevronStyle = css({
  marginLeft: 'auto',
});

const numberInCircleStyles = css({
  fontSize: 14,
});

const tooltipContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const ActiveBoundaryDropdownTriggerComponent = ({
  onClick,
  entities,
  fillOrderNumber,
  foregroundColor,
  backgroundColor,
}: ActiveBoundaryDropdownTrigger) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const mainEntityRef = useRef<HTMLDivElement>(null);
  const subEntityRef = useRef<HTMLDivElement>(null);

  const { shortenedSubEntity, shortenedMainEntity, mainEntity } = entities;

  const checkOverflowVisibility = useCallback(() => {
    const mainEntityElement = mainEntityRef.current;
    const subEntityElement = subEntityRef.current;

    if (mainEntityElement && (
      (mainEntityElement.scrollWidth > mainEntityElement.clientWidth) || (mainEntityElement.scrollHeight > mainEntityElement.clientHeight)
    )) {
      setIsOverflowing(true);
    }
    else if (subEntityElement && (
      (subEntityElement.scrollWidth > subEntityElement.clientWidth) || (subEntityElement.scrollHeight > subEntityElement.clientHeight)
    )) {
      setIsOverflowing(true);
    }
    else {
      setIsOverflowing(false);
    }
  }, []);

  useEffect(() => {
    const _ = entities;
    checkOverflowVisibility();
  }, [checkOverflowVisibility, entities]);

  return (
    <TooltipComponent
      behavior={TooltipBehavior.ShowOnHover}
      tooltipContent={isOverflowing ? (
        <span css={tooltipContentStyles}>
          <span>
            {shortenedMainEntity}
          </span>
          {shortenedSubEntity && (
            <FontAwesomeIcon
              icon={faChevronRight}
              size="xs"
            />
          )}
          {shortenedSubEntity && (
            <span>
              {shortenedSubEntity}
            </span>
          )}
        </span>
      ) : ''}
    >
      <div
        ref={triggerRef}
        css={[ddTriggerStyles, onClick ? interactiveDdTriggerStyles : undefined]}
        onClick={onClick}
      >
        {(fillOrderNumber !== null) && (
          <NumberInCircleComponent
            css={numberInCircleStyles}
            colors={{
              fontColor: foregroundColor,
              fillColor: backgroundColor,
            }}
            size={18}
          >
            {fillOrderNumber}
          </NumberInCircleComponent>
        )}
        <div css={entityWrapperStyles}>
          <div css={mainEntityStyles}>
            <div
              css={ellipsisStyle}
              ref={mainEntityRef}
            >
              {mainEntity}
            </div>
            {shortenedSubEntity && (
              <FontAwesomeIcon
                css={entityChevronStyle}
                icon={faChevronRight}
                size="xs"
              />
            )}
          </div>
          {shortenedSubEntity && (
            <div
              css={[subEntityStyles, ellipsisStyle]}
              ref={subEntityRef}
            >
              {shortenedSubEntity}
            </div>
          )}
        </div>
        {onClick && (
          <FontAwesomeIcon
            css={chevronStyle}
            icon={faChevronDown}
            size="lg"
          />
        )}
      </div>
    </TooltipComponent>
  );
};
